import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { formatRelativeTime, formatUptime } from "../lib/format";
import { metricStatus, statusOf } from "../lib/status";
import { I, StatusBadge } from "./primitives";

/**
 * Format seconds into HH:MM:SS (top-style TIME+ column).
 */
function formatRunTime(totalSec) {
  if (totalSec == null || totalSec < 0) return "—";
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function ProcessPanel({ server, thresholds, onClose }) {
  const [tab, setTab] = useState("processes");
  const [procs, setProcs] = useState([]);
  const [procsUpdatedAt, setProcsUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cmdResult, setCmdResult] = useState(null);
  const [cmdLoading, setCmdLoading] = useState(null);

  const fetchProcs = useCallback(async (serverId) => {
    try {
      setLoading(true);
      const data = await api.serverProcesses(serverId);
      setProcs(data.processes || []);
      setProcsUpdatedAt(data.updated_at);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll process data every 5 seconds while the panel is open.
  useEffect(() => {
    if (!server || server.offline) {
      setProcs([]);
      setProcsUpdatedAt(null);
      return;
    }
    fetchProcs(server.id);
    const t = setInterval(() => fetchProcs(server.id), 5000);
    return () => clearInterval(t);
  }, [server?.id, server?.offline, fetchProcs]);

  const open = !!server;

  return (
    <Fragment>
      <div className="panel-backdrop" data-open={open} onClick={onClose} />
      <aside className="proc-panel" data-open={open}>
        {server && (
          <Fragment>
            <div className="proc-head">
              <span className="server-status-pip" data-status={statusOf(server, thresholds)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <span className="server-name">{server.name}</span>
                  <span className="server-tag" data-role={server.role}>{server.tag}</span>
                  {server.wsConnected != null && (
                    <span
                      title={server.wsConnected ? "Agent WS connected" : "Agent WS disconnected"}
                      style={{
                        display: "inline-block",
                        width: 8, height: 8,
                        borderRadius: "50%",
                        background: server.wsConnected ? "var(--ok, #34c759)" : "var(--text-3, #888)",
                        marginLeft: 4,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
                <div className="server-addr">
                  {server.host} · {server.ip}:{server.port}
                </div>
              </div>
              <button
                className="btn-icon bare"
                title="Refresh"
                onClick={() => !server.offline && fetchProcs(server.id)}
              >
                <I.refresh />
              </button>
              <button className="btn-icon bare" onClick={onClose} title="Close"><I.close /></button>
            </div>

            <div className="proc-meta">
              <div>
                <div className="proc-meta-label">Uptime</div>
                <div className="proc-meta-val">{formatUptime(server.uptimeSec)}</div>
              </div>
              <div>
                <div className="proc-meta-label">Cores</div>
                <div className="proc-meta-val">{server.cores || "—"}</div>
              </div>
              <div>
                <div className="proc-meta-label">Memori</div>
                <div className="proc-meta-val">{server.ramGb ? `${server.ramGb} GB` : "—"}</div>
              </div>
              <div>
                <div className="proc-meta-label">Disk</div>
                <div className="proc-meta-val">{server.diskGb ? `${server.diskGb} GB` : "—"}</div>
              </div>
            </div>

            <div className="proc-tabs">
              <button className="proc-tab" aria-current={tab === "processes" ? "page" : undefined} onClick={() => setTab("processes")}>Proses</button>
              <button className="proc-tab" aria-current={tab === "metrics" ? "page" : undefined} onClick={() => setTab("metrics")}>Metrik</button>
              <button className="proc-tab" aria-current={tab === "info" ? "page" : undefined} onClick={() => setTab("info")}>Info</button>
              <button className="proc-tab" aria-current={tab === "control" ? "page" : undefined} onClick={() => setTab("control")}>Control</button>
            </div>

            <div className="proc-body">
              {tab === "processes" && (
                <Fragment>
                  <div style={{
                    padding: "10px 20px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    color: "var(--text-3)",
                    borderBottom: "1px solid var(--border-soft)",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <span>top - {new Date().toTimeString().slice(0, 8)} up {formatUptime(server.uptimeSec)}</span>
                    <span>·</span>
                    <span>{procs.length} tasks</span>
                    <span>·</span>
                    <span>
                      load avg: {server.load
                        ? `${server.load.one.toFixed(2)}, ${server.load.five.toFixed(2)}, ${server.load.fifteen.toFixed(2)}`
                        : "—"}
                    </span>
                    {procsUpdatedAt && (
                      <>
                        <span>·</span>
                        <span style={{ color: "var(--text-3)" }} title={`Last update: ${new Date(procsUpdatedAt).toLocaleTimeString()}`}>
                          {formatRelativeTime(procsUpdatedAt)}
                        </span>
                      </>
                    )}
                  </div>

                  {loading && procs.length === 0 && (
                    <div style={{ padding: "32px 20px", color: "var(--text-3)", fontSize: 13, textAlign: "center" }}>
                      Memuat data proses…
                    </div>
                  )}

                  {error && (
                    <div style={{ padding: "12px 20px", color: "var(--warn)", fontSize: 12 }}>
                      Gagal memuat: {error.message || "unknown error"}
                    </div>
                  )}

                  {!loading && !error && procs.length === 0 && (
                    <div style={{ padding: "32px 20px", color: "var(--text-3)", fontSize: 13, textAlign: "center" }}>
                      Agent belum mengirim data proses.
                      <br />
                      <span style={{ fontSize: 12 }}>Pastikan agent versi terbaru sedang berjalan.</span>
                    </div>
                  )}

                  {procs.length > 0 && (
                    <table className="procs">
                      <thead>
                        <tr>
                          <th style={{ width: 64 }}>PID</th>
                          <th style={{ width: 90 }}>USER</th>
                          <th style={{ width: 70 }} className="pcpu">%CPU</th>
                          <th style={{ width: 70 }} className="pram">%MEM</th>
                          <th style={{ width: 80 }}>TIME+</th>
                          <th>COMMAND</th>
                        </tr>
                      </thead>
                      <tbody>
                        {procs.map((p) => (
                          <tr key={p.pid}>
                            <td className="pid">{p.pid}</td>
                            <td className="pid">{p.user}</td>
                            <td
                              className="pcpu"
                              style={{
                                color: p.cpu > 60 ? "var(--crit)" : p.cpu > 30 ? "var(--warn)" : "var(--text)",
                                fontWeight: p.cpu > 30 ? 600 : 400,
                              }}
                            >
                              {p.cpu.toFixed(1)}
                            </td>
                            <td className="pram">{p.ram.toFixed(1)}</td>
                            <td className="pid">{formatRunTime(p.run_time_sec)}</td>
                            <td className="pname">{p.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Fragment>
              )}

              {tab === "metrics" && (
                <MetricsTab server={server} thresholds={thresholds} />
              )}

              {tab === "info" && (
                <div className="proc-charts">
                  <div className="proc-chart-card">
                    <div className="proc-chart-title" style={{ marginBottom: 10 }}>Konfigurasi</div>
                    <InfoRow label="Hostname" value={server.host} />
                    <InfoRow label="IP Address" value={`${server.ip}:${server.port}`} />
                    <InfoRow label="Region" value={server.region} />
                    <InfoRow label="OS" value={server.os} />
                    <InfoRow label="Kernel" value={server.kernel || "—"} />
                    <InfoRow label="Arsitektur" value={server.arch || "—"} />
                    <InfoRow label="vCPU / Memori / Disk" value={`${server.cores || "?"} core · ${server.ramGb || "?"} GB · ${server.diskGb || "?"} GB`} />
                    <InfoRow label="Uptime" value={formatUptime(server.uptimeSec)} />
                    <InfoRow label="Load avg (1/5/15m)" value={server.load
                      ? `${server.load.one.toFixed(2)} · ${server.load.five.toFixed(2)} · ${server.load.fifteen.toFixed(2)}`
                      : "—"} />
                    <InfoRow label="Tag" value={server.tag} />
                    <InfoRow label="Agent" value={server.agentVersion ? `monibot-agent v${server.agentVersion}` : "—"} />
                    <InfoRow label="Last Seen" value={server.lastSeen ? formatRelativeTime(server.lastSeen) : "—"} />
                    <InfoRow label="Status" value={<StatusBadge status={statusOf(server, thresholds)} />} />
                    <InfoRow label="WS Channel" value={
                      <span style={{ color: server.wsConnected ? "var(--ok, #34c759)" : "var(--text-3)" }}>
                        {server.wsConnected ? "● Connected" : "○ Disconnected"}
                      </span>
                    } />
                  </div>
                </div>
              )}

              {tab === "control" && (
                <ControlTab
                  server={server}
                  cmdResult={cmdResult}
                  setCmdResult={setCmdResult}
                  cmdLoading={cmdLoading}
                  setCmdLoading={setCmdLoading}
                />
              )}
            </div>
          </Fragment>
        )}
      </aside>
    </Fragment>
  );
}

// ── Metrik tab — range selector + 3 enlarged charts (CPU/RAM/DISK) ──────────
// "Live" reuses the in-memory ring buffer that useServers maintains for every
// open server (no extra fetch, refreshes every 3s with the dashboard tick).
// 24h / 3d / 7d hit the new aggregated endpoint and cache per (serverId, range).

const RANGES = [
  { id: "live", label: "Live", xTicks: ["", "now"] },
  { id: "24h",  label: "24 Jam", xTicks: ["-24j", "-18j", "-12j", "-6j", "now"] },
  { id: "3d",   label: "3 Hari", xTicks: ["-3d", "-2d", "-1d", "now"] },
  { id: "7d",   label: "7 Hari", xTicks: ["-7d", "-5d", "-3d", "-1d", "now"] },
];

function MetricsTab({ server, thresholds }) {
  const [range, setRange] = useState("live");
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch aggregated points whenever the range changes (except live, which
  // uses the live ring buffer from useServers polling).
  useEffect(() => {
    if (range === "live") {
      setPoints(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .serverMetrics(server.id, { range })
      .then((data) => {
        if (cancelled) return;
        setPoints(data.points || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [server.id, range]);

  const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[0];

  // Pick the data series for each metric. Live uses the in-memory history
  // arrays from useServers; aggregated ranges use the fetched points[].
  const seriesFor = (metric) => {
    if (range === "live") {
      const key = metric === "cpu" ? "historyCpu" : metric === "ram" ? "historyRam" : "historyDisk";
      return server[key] ?? [];
    }
    if (!points) return [];
    return points.map((p) => p[metric]);
  };

  return (
    <div className="proc-charts">
      <div className="metric-range-tabs" role="tablist">
        {RANGES.map((r) => (
          <button
            key={r.id}
            className="metric-range-tab"
            type="button"
            role="tab"
            aria-selected={range === r.id}
            onClick={() => setRange(r.id)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 8,
          background: "var(--crit-soft)", color: "var(--crit)",
          fontSize: 12,
        }}>
          Gagal memuat data: {error.message || "unknown error"}
        </div>
      )}

      <MetricChartCard
        label="CPU"
        value={server.cpu}
        data={seriesFor("cpu")}
        threshold={thresholds.cpu}
        xTicks={rangeMeta.xTicks}
        loading={loading}
      />
      <MetricChartCard
        label="RAM"
        value={server.ram}
        data={seriesFor("ram")}
        threshold={thresholds.ram}
        xTicks={rangeMeta.xTicks}
        loading={loading}
      />
      <MetricChartCard
        label="DISK"
        value={server.disk}
        data={seriesFor("disk")}
        threshold={thresholds.disk}
        xTicks={rangeMeta.xTicks}
        loading={loading}
      />
    </div>
  );
}

function MetricChartCard({ label, value, data, threshold, xTicks, loading }) {
  const status = value == null ? "offline" : metricStatus(value, threshold);
  const color =
    status === "crit"
      ? "oklch(0.68 0.20 25)"
      : status === "warn"
        ? "oklch(0.83 0.14 80)"
        : status === "offline"
          ? "oklch(0.5 0.01 250)"
          : "oklch(0.78 0.14 155)";

  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;
    let min = Infinity, max = -Infinity, sum = 0, n = 0;
    for (const v of data) {
      if (v == null || Number.isNaN(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
      sum += v;
      n++;
    }
    if (n === 0) return null;
    return { min, max, avg: sum / n };
  }, [data]);

  return (
    <div className="proc-chart-card metric-chart-card">
      <div className="proc-chart-head">
        <div className="proc-chart-title">{label}</div>
        <div className="proc-chart-val" style={{ color }}>
          {value == null ? "—" : Math.round(value) + "%"}
        </div>
      </div>
      <MetricChartSVG
        data={data}
        color={color}
        threshold={threshold}
        xTicks={xTicks}
        loading={loading}
      />
      {stats && (
        <div className="metric-chart-stats">
          <span className="metric-stat">
            <span className="metric-stat-label">MIN</span>
            <span className="metric-stat-val">{Math.round(stats.min)}%</span>
          </span>
          <span className="metric-stat">
            <span className="metric-stat-label">AVG</span>
            <span className="metric-stat-val">{Math.round(stats.avg)}%</span>
          </span>
          <span className="metric-stat">
            <span className="metric-stat-label">MAX</span>
            <span className="metric-stat-val" style={{ color: stats.max >= threshold ? "var(--crit)" : undefined }}>
              {Math.round(stats.max)}%
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

function MetricChartSVG({ data, color, threshold, xTicks, loading }) {
  // viewBox is fixed; the actual rendered size scales via CSS width:100%.
  const W = 500;
  const H = 180;
  const PAD_L = 32;
  const PAD_R = 12;
  const PAD_T = 8;
  const PAD_B = 22;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const yTicks = [0, 25, 50, 75, 100];
  const yPos = (v) => PAD_T + (1 - v / 100) * plotH;

  const hasData = data && data.length >= 2;
  const stepX = hasData ? plotW / (data.length - 1) : 0;
  const points = hasData
    ? data.map((v, i) => ({
        x: PAD_L + i * stepX,
        y: yPos(Math.max(0, Math.min(100, v ?? 0))),
      }))
    : [];

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const areaPath = hasData
    ? `${linePath} L ${PAD_L + plotW} ${PAD_T + plotH} L ${PAD_L} ${PAD_T + plotH} Z`
    : "";

  const thresholdY = yPos(threshold);
  const gradId = `grad-${color.replace(/[^a-z0-9]/gi, "")}-${threshold}`;

  return (
    <svg
      className="metric-chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* y-axis tick labels + horizontal gridlines */}
      {yTicks.map((t) => {
        const y = yPos(t);
        return (
          <g key={t}>
            <line
              x1={PAD_L}
              x2={PAD_L + plotW}
              y1={y}
              y2={y}
              stroke="var(--border-soft)"
              strokeWidth="1"
              strokeDasharray={t === 0 ? "0" : "2 4"}
              opacity={t === 0 ? 0.5 : 0.25}
            />
            <text
              x={PAD_L - 6}
              y={y + 3}
              fill="var(--text-3)"
              fontSize="10"
              fontFamily="var(--font-mono)"
              textAnchor="end"
            >
              {t}
            </text>
          </g>
        );
      })}

      {/* x-axis tick labels (evenly spaced across plot width) */}
      {xTicks.map((label, i) => {
        const x = PAD_L + (i / Math.max(1, xTicks.length - 1)) * plotW;
        const anchor = i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle";
        return (
          <text
            key={`${label}-${i}`}
            x={x}
            y={H - 6}
            fill="var(--text-3)"
            fontSize="10"
            fontFamily="var(--font-mono)"
            textAnchor={anchor}
          >
            {label}
          </text>
        );
      })}

      {/* threshold dashed line + inline label */}
      <line
        x1={PAD_L}
        x2={PAD_L + plotW}
        y1={thresholdY}
        y2={thresholdY}
        stroke={color}
        strokeWidth="1"
        strokeDasharray="4 4"
        opacity="0.55"
      />
      <text
        x={PAD_L + plotW - 4}
        y={thresholdY - 4}
        fill={color}
        fontSize="10"
        fontFamily="var(--font-mono)"
        textAnchor="end"
        opacity="0.85"
      >
        threshold {threshold}%
      </text>

      {/* area + line */}
      {hasData && (
        <Fragment>
          <path d={areaPath} fill={`url(#${gradId})`} />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </Fragment>
      )}

      {/* empty / loading state */}
      {!hasData && (
        <text
          x={PAD_L + plotW / 2}
          y={PAD_T + plotH / 2}
          fill="var(--text-3)"
          fontSize="12"
          fontFamily="var(--font-mono)"
          textAnchor="middle"
        >
          {loading ? "memuat…" : "tidak ada data"}
        </text>
      )}
    </svg>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center" style={{
      padding: "8px 0",
      borderBottom: "1px solid var(--border-soft)",
      fontSize: 13,
    }}>
      <span style={{ color: "var(--text-3)", width: 200, flexShrink: 0 }}>{label}</span>
      <span className="mono" style={{ wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}

const CMD_BUTTONS = [
  {
    type: "ping",
    label: "🏓 Ping",
    desc: "Test roundtrip latency ke agent",
    color: "var(--accent, #34c759)",
  },
  {
    type: "config",
    label: "⚙️ Config",
    desc: "Kirim konfigurasi update",
    color: "oklch(0.78 0.10 240)",
  },
  {
    type: "restart",
    label: "🔄 Restart",
    desc: "Restart agent process (supervisor harus aktif)",
    color: "oklch(0.68 0.20 25)",
    confirm: true,
  },
];

function ControlTab({ server, cmdResult, setCmdResult, cmdLoading, setCmdLoading }) {
  const [confirmRestart, setConfirmRestart] = useState(false);

  const runCommand = useCallback(async (type) => {
    setCmdLoading(type);
    setCmdResult(null);
    const start = Date.now();
    try {
      const cmd = { type };
      const result = await api.sendCommand(server.id, cmd);
      const latency = Date.now() - start;
      setCmdResult({
        type,
        ok: result.ok,
        latency,
        error: result.error,
        commandId: result.command_id,
      });
    } catch (e) {
      setCmdResult({
        type,
        ok: false,
        latency: Date.now() - start,
        error: e.message || "Unknown error",
      });
    } finally {
      setCmdLoading(null);
      setConfirmRestart(false);
    }
  }, [server?.id, setCmdLoading, setCmdResult]);

  const wsOk = !!server?.wsConnected;

  return (
    <div className="proc-charts">
      <div className="proc-chart-card">
        <div className="proc-chart-title" style={{ marginBottom: 6 }}>Kontrol Agent</div>
        <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.5 }}>
          Kirim perintah ke agent via WebSocket.
          {!wsOk && (
            <span style={{ color: "var(--warn, #e8a735)", display: "block", marginTop: 4 }}>
              ⚠ Agent tidak terhubung via WS — perintah tidak bisa dikirim.
            </span>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {CMD_BUTTONS.map((btn) => {
            const isLoading = cmdLoading === btn.type;
            const isConfirm = btn.confirm && btn.type === "restart";
            const needsConfirm = isConfirm && !confirmRestart;

            return (
              <div key={btn.type} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid var(--border-soft)",
                background: "var(--surface-2, rgba(255,255,255,0.04))",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{btn.label}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{btn.desc}</div>
                </div>
                {needsConfirm ? (
                  <button
                    disabled={!wsOk || isLoading}
                    onClick={() => setConfirmRestart(true)}
                    style={{
                      padding: "6px 16px", borderRadius: 7, border: "none",
                      background: btn.color, color: "#fff", fontWeight: 600,
                      fontSize: 12, cursor: wsOk ? "pointer" : "not-allowed",
                      opacity: wsOk ? 1 : 0.4,
                    }}
                  >
                    Kirim
                  </button>
                ) : isConfirm && confirmRestart ? (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => setConfirmRestart(false)}
                      style={{
                        padding: "6px 12px", borderRadius: 7, border: "1px solid var(--border-soft)",
                        background: "transparent", color: "var(--text)", fontSize: 12, cursor: "pointer",
                      }}
                    >
                      Batal
                    </button>
                    <button
                      disabled={isLoading}
                      onClick={() => runCommand(btn.type)}
                      style={{
                        padding: "6px 12px", borderRadius: 7, border: "none",
                        background: btn.color, color: "#fff", fontWeight: 600,
                        fontSize: 12, cursor: "pointer",
                      }}
                    >
                      {isLoading ? "..." : "Yakin?"}
                    </button>
                  </div>
                ) : (
                  <button
                    disabled={!wsOk || isLoading}
                    onClick={() => runCommand(btn.type)}
                    style={{
                      padding: "6px 16px", borderRadius: 7, border: "none",
                      background: btn.color, color: "#fff", fontWeight: 600,
                      fontSize: 12, cursor: wsOk ? "pointer" : "not-allowed",
                      opacity: wsOk ? 1 : 0.4,
                    }}
                  >
                    {isLoading ? "Mengirim..." : "Kirim"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {cmdResult && (
          <div style={{
            marginTop: 16, padding: "10px 14px", borderRadius: 8,
            background: cmdResult.ok ? "rgba(52,199,89,0.08)" : "rgba(255,69,58,0.08)",
            border: `1px solid ${cmdResult.ok ? "rgba(52,199,89,0.3)" : "rgba(255,69,58,0.3)"}`,
            fontSize: 12, fontFamily: "var(--font-mono)",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: cmdResult.ok ? "var(--ok, #34c759)" : "var(--crit, #ff453a)" }}>
              {cmdResult.ok ? "✓ Success" : "✕ Failed"} — {cmdResult.type}
            </div>
            <div style={{ color: "var(--text-3)" }}>
              Latency: {cmdResult.latency}ms
              {cmdResult.commandId && <> · ID: {cmdResult.commandId}</>}
              {cmdResult.error && <div style={{ marginTop: 4, color: "var(--crit, #ff453a)" }}>Error: {cmdResult.error}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
